import photosJson from './photos.json';

/** A photo shown on a place page, with the credit its licence asks for (see scripts/build-place-photos.mjs). */
export type Photo = { file: string; alt: string; credit: string; license: string; licenseUrl: string; source: string };

/** A place on the voyage, written for family deciding where to join us. Ports tie it to the planner's stops. */
export type Place = {
  slug: string;
  name: string;
  country: string;
  ports: string[];
  tagline: string;
  intro: string[];
  highlights: { title: string; text: string }[];
  photos: Photo[];
};

type Story = Omit<Place, 'photos'>;

/** In route order, Nouméa to Singapore. */
const STORIES: Story[] = [
  {
    slug: 'new-caledonia', name: 'Nouméa', country: 'New Caledonia (France)', ports: ['NOU'],
    tagline: 'Where the next leg begins: a French city on one of the biggest lagoons on Earth.',
    intro: [
      'COS arrived here from Fiji in October 2026: Nouméa, where croissants and baguettes meet coral reef. The island of Grande Terre is ringed by a barrier reef so long that its lagoons are a UNESCO World Heritage site.',
      'Until we leave we are getting COS ready, and between jobs we swim off Anse Vata, sail out to the lighthouse on Amédée islet and spend weekends anchored in the lagoon.',
    ],
    highlights: [
      { title: 'The lagoon', text: 'Turquoise water inside the reef, turtles, rays and coral heads a short sail from the city.' },
      { title: 'Amédée Lighthouse', text: 'A cast-iron lighthouse built in Paris, shipped out in pieces and lit in 1865. Climb it for the view across the reef.' },
      { title: 'Port Moselle market', text: 'Fish, fruit and French pastries at the market by the marina.' },
      { title: 'Tjibaou Cultural Centre', text: "Renzo Piano's soaring wooden huts celebrate Kanak culture on a peninsula outside Nouméa." },
    ],
  },
  {
    slug: 'tanna', name: 'Tanna', country: 'Vanuatu', ports: ['TAN'],
    tagline: 'Stand on the rim of a live volcano at dusk, then sleep in a bay Captain Cook named after his ship.',
    intro: [
      'Tanna is raw and unforgettable. Mount Yasur has been erupting for centuries, and you can walk up to the crater rim and watch it throw glowing lava into the night sky, close enough to feel the boom in your chest.',
      'We anchor at Port Resolution, named by Captain Cook in 1774 after HMS Resolution. Villages here still live by kastom, the island grows its own coffee, and the welcome is as warm as the ground.',
    ],
    highlights: [
      { title: 'Mount Yasur at dusk', text: 'The best show on the voyage: fountains of lava, thunder from the crater, and stars overhead.' },
      { title: 'Port Resolution', text: 'A sheltered bay with hot springs on the shore and a small village yacht club.' },
      { title: 'Kastom villages', text: 'Communities that keep traditional dress, dance and law; visits are by invitation and with a guide.' },
      { title: 'Tanna coffee', text: 'Grown in volcanic soil and roasted on the island. A good souvenir to carry home.' },
    ],
  },
  {
    slug: 'efate', name: 'Port Vila and Efate', country: 'Vanuatu', ports: ['VIL'],
    tagline: "Vanuatu's easy-going capital, with waterfalls, blue lagoons and a post office under the sea.",
    intro: [
      'Port Vila is the friendliest capital in the Pacific and the easiest place on the route to fly into. The harbour is full of yachts, the market is piled with fruit, and the island around it is made for day trips.',
      'It is the easiest place on the route to fly into, with direct flights from Australia, New Zealand and Fiji, and there is plenty to do around the island.',
    ],
    highlights: [
      { title: 'Mele Cascades', text: 'Climb beside turquoise pools and waterfalls stepping down through the rainforest.' },
      { title: 'Hideaway Island', text: 'Snorkel a marine reserve and post a waterproof postcard in the underwater post office.' },
      { title: 'Blue Lagoon', text: 'A vivid freshwater-and-sea lagoon with rope swings, on the drive around the island.' },
      { title: 'Port Vila market', text: 'Open around the clock most of the week, with local fruit, kava and island cooking.' },
    ],
  },
  {
    slug: 'malekula-ambrym', name: 'Ambrym', country: 'Vanuatu', ports: ['AMB'],
    tagline: 'Twin volcanoes, carved slit drums and the island Vanuatu calls the home of magic.',
    intro: [
      'Ambrym rises dark and dramatic under twin volcanoes, Marum and Benbow, whose ash plains and black-sand beaches make it feel like nowhere else in Vanuatu. People across the islands call it the home of magic, and treat its sorcerers with respect.',
      'It is also the home of some of the greatest art in the Pacific: tall carved slit drums, the masked Rom dance, and sand drawings, a single unbroken line traced in the sand to tell a story, which UNESCO lists as world heritage.',
    ],
    highlights: [
      { title: 'Rom dance', text: 'Masked dancers in banana-leaf cloaks, if a festival falls while we are there.' },
      { title: 'Sand drawings', text: 'A single unbroken line traced in the sand that tells a story or a map, without lifting the finger.' },
      { title: 'Slit drums', text: "Ambrym's tall carved tam-tams are some of the most striking art in the Pacific." },
      { title: 'Volcano country', text: 'Black-sand beaches and ash plains under Marum and Benbow.' },
    ],
  },
  {
    slug: 'champagne-beach', name: 'Champagne Beach and Santo\'s east coast', country: 'Vanuatu', ports: ['CHB'],
    tagline: 'White sand, glass-clear water and a week at anchor off the most beautiful beach in Vanuatu.',
    intro: [
      "Champagne Beach curves around a sheltered bay at Hog Harbour, on the north-east coast of Espiritu Santo. The sand is powder-white, the water is so clear that the boat seems to float in air, and at low tide bubbles rise through the sand, which is how the beach got its name.",
      "We anchor in the bay and stay a week. The coast north of here is all white beaches and quiet villages: Port Olry has a long sweep of sand and a couple of beach-shack restaurants, and its offshore islets are a short dinghy ride away. Inland, the Matevulu and Nanda blue holes are spring-fed pools of startling blue in the forest.",
      "Mornings are for swimming off the back of the boat, afternoons for exploring by dinghy, and evenings for the sunset over the hills of Santo.",
    ],
    highlights: [
      { title: 'Champagne Beach', text: 'Arguably the best beach in the Pacific, and we anchor right off it.' },
      { title: 'Port Olry', text: 'Long white beaches, islets to snorkel and fresh lobster ashore.' },
      { title: 'Blue holes', text: 'Matevulu and Nanda: clear spring pools in the jungle with rope swings.' },
      { title: 'Life at anchor', text: 'Paddleboards, snorkelling straight off the stern and nowhere to be.' },
    ],
  },
  {
    slug: 'banks', name: 'Sola and the Banks Islands', country: 'Vanuatu', ports: ['SOL'],
    tagline: "Vanuatu's far north: a tiny provincial capital, volcanoes and the home of water music.",
    intro: [
      "Sola, on Vanua Lava, is the capital of Torba, Vanuatu's northernmost province: a few buildings, an airstrip and a quiet bay. The island rises steeply to Mount Suretamatai, a volcano, and rivers and waterfalls run down its slopes to the sea.",
      "The Banks Islands are where Vanuatu's water music comes from: women standing waist-deep in the sea, slapping and scooping the water to make rhythms like drums. Life here is simple, and visitors are rare.",
    ],
    highlights: [
      { title: 'Water music', text: 'A rare performance made with nothing but hands and the sea.' },
      { title: 'Waterfalls', text: 'Rivers and falls tumbling off the volcanic slopes of Vanua Lava.' },
      { title: 'Village life', text: 'Gardens, canoes and a warm welcome far from the tourist trail.' },
    ],
  },
  {
    slug: 'luganville', name: 'Luganville', country: 'Vanuatu', ports: ['SAN'],
    tagline: "Vanuatu's second town, a sunken troopship and a beach of wartime wreckage.",
    intro: [
      "Luganville, on the south coast of Espiritu Santo, is Vanuatu's second town: a long main street built for the Second World War, a busy market and the last good shops for a while. Just offshore lies the SS President Coolidge, a huge troopship that sank in 1942 and is now one of the world's great wreck dives.",
      'Along the coast is Million Dollar Point, where the US Army pushed its surplus equipment into the sea at the end of the war rather than sell it cheaply. Bulldozers, trucks and crates now lie on the reef in shallow water.',
    ],
    highlights: [
      { title: 'President Coolidge', text: 'A famous wreck dive: a whole troopship on its side in clear water.' },
      { title: 'Million Dollar Point', text: 'A beach of drowned wartime equipment to snorkel.' },
      { title: 'Luganville market', text: 'Fruit, vegetables and kava from all over Santo.' },
    ],
  },
  {
    slug: 'vanikoro', name: 'Vanikoro and Utupua', country: 'Solomon Islands', ports: ['VAN', 'UTU'],
    tagline: 'Remote high islands in the far east of the Solomons, and the reef where La Pérouse vanished.',
    intro: [
      "Vanikoro is a mountainous, forested island ringed by a barrier reef in Temotu's Santa Cruz group. In 1788 the French explorer La Pérouse's two ships, the Boussole and the Astrolabe, were wrecked on its reef, and what became of the expedition stayed a mystery until the 1820s, when Peter Dillon and then Dumont d'Urville traced the wrecks here.",
      'Utupua, just to the west, is another high island with deep, sheltered bays and a few small villages. Both see very few visitors, and a yacht arriving is news.',
    ],
    highlights: [
      { title: "La Pérouse's wrecks", text: 'The reef where the expedition was lost, and the memorial Dumont d\'Urville left ashore in 1828.' },
      { title: 'Rainforest and rivers', text: 'High, green islands with rivers to explore by dinghy.' },
      { title: 'Sheltered bays', text: "Utupua's deep, quiet anchorages." },
      { title: 'Village life', text: 'Small villages, gardens and canoes, far from anywhere.' },
    ],
  },
  {
    slug: 'temotu', name: 'Lata and the Reef Islands', country: 'Solomon Islands', ports: ['LAT', 'REF'],
    tagline: 'Our way into the Solomons: a tiny capital, then low coral islands at the edge of the map.',
    intro: [
      "Lata, on Nendo in the Santa Cruz Islands, is the capital of Temotu, the Solomon Islands' easternmost province, and where we clear in to the country: a small town with a market, a few shops and a very relaxed pace.",
      'North of Nendo, the Reef Islands are a scatter of low coral islands around wide, clear lagoons. Some are Melanesian and some, like Pileni and Nifiloli, are Polynesian, settled by people who sailed here in voyaging canoes. Few yachts ever come this way.',
      'These islands are also where the famous red feather money was made: long coils of tiny scarlet feathers, once used as bride price across the region.',
    ],
    highlights: [
      { title: 'Clearing in at Lata', text: 'Customs, a market for fresh fruit and our first taste of the Solomons.' },
      { title: 'Reef Islands lagoons', text: 'Clear, shallow water, sandbanks and reefs we will have to ourselves.' },
      { title: 'Canoes', text: 'Outrigger canoes are still how people get around the lagoons.' },
      { title: 'Feather money', text: 'Coils of red feathers once used as bride price, made only in these islands.' },
    ],
  },
  {
    slug: 'honiara', name: 'Honiara', country: 'Solomon Islands', ports: ['HON'],
    tagline: 'The capital: a busy market, Second World War history and a wreck you can snorkel from the beach.',
    intro: [
      'Honiara, on the north coast of Guadalcanal, is the capital and the one real town in the Solomons: the place for fresh supplies, fuel, flights and paperwork. Its Central Market is piled with fruit, betel nut, fish and shell money.',
      'Guadalcanal was the scene of one of the longest campaigns of the Second World War, in 1942 and 1943. The sea off Honiara is called Iron Bottom Sound for the dozens of warships sunk in it, and at Bonegi, just west of town, Japanese transports lie in shallow water off the beach where they were run aground.',
    ],
    highlights: [
      { title: 'Central Market', text: 'Fruit, fish, betel nut and shell money, all in one busy hall.' },
      { title: 'Bonegi wrecks', text: 'Snorkel a wartime transport straight off the beach.' },
      { title: 'Wartime memorials', text: 'The American memorial on Skyline Ridge and the Japanese peace memorial on Mount Austen.' },
    ],
  },
  {
    slug: 'russells', name: 'The Russell Islands', country: 'Solomon Islands', ports: ['RUS'],
    tagline: 'Quiet plantation islands, calm passages and a Second World War past.',
    intro: [
      'The Russell Islands lie north-west of Guadalcanal: two main islands, Pavuvu and Banika, cut by narrow passages and fringed with small islets and reef. Much of the land is coconut plantation, and the villages are small and welcoming.',
      'In 1943 and 1944 the islands were a big American base, and the 1st Marine Division rested on Pavuvu before the battle of Peleliu. Rusting relics still turn up in the bush and on the reef, and the passages are clear and calm, good for snorkelling straight off the boat.',
    ],
    highlights: [
      { title: 'Sheltered passages', text: 'Anchor among the islets and snorkel the channels.' },
      { title: 'Wartime relics', text: 'Remains of the American bases on Banika and Pavuvu.' },
      { title: 'Village visits', text: 'Fruit, carvings and a warm welcome.' },
    ],
  },
  {
    slug: 'marovo', name: 'Marovo Lagoon, Uepi and Noro', country: 'Solomon Islands', ports: ['MAR', 'UEP', 'NOR'],
    tagline: 'A vast lagoon of jungle islands, master carvers and one of the best dives in the Solomons.',
    intro: [
      "Marovo Lagoon is often called the world's largest saltwater lagoon: hundreds of jungle-covered islands behind a double barrier reef, around New Georgia and Vangunu. The villages are known for some of the finest woodcarving in the Pacific.",
      "Uepi sits on the lagoon's outer edge, where the barrier reef drops into deep water. The drop-off at Uepi Point is one of the most famous dives in the Solomons, with sharks, turtles and huge schools of fish, and the lagoon side is perfect for snorkelling and paddling.",
      'Noro, on the north-west coast of New Georgia, is a working port with a tuna cannery, and our stop for fuel and supplies.',
    ],
    highlights: [
      { title: 'Paddling the lagoon', text: 'Kayak and paddleboard between islands, villages and clear passages.' },
      { title: 'Nguzunguzu carvings', text: 'Canoe-prow guardian figures inlaid with shell, a symbol of the Western Province.' },
      { title: 'Uepi Point', text: 'A wall of coral dropping into the deep, a short swim from the beach.' },
      { title: 'Noro', text: 'Fuel, fresh food and a look at a busy Solomons port.' },
    ],
  },
  {
    slug: 'gizo', name: 'Gizo', country: 'Solomon Islands', ports: ['GIZ'],
    tagline: 'The friendly capital of the Western Province, with great diving and the island where JFK swam ashore.',
    intro: [
      'Gizo is the capital of the Western Province: a small, friendly waterfront town with a market, a few shops and dive operators, and a good anchorage in its harbour.',
      'Around it are clear water, reefs and wrecks. Off Gizo is Kennedy Island, where a young John F. Kennedy and his crew swam ashore after PT-109 was sunk in 1943, and the Japanese transport Toa Maru lies on its side in shallow water nearby.',
    ],
    highlights: [
      { title: 'Kennedy Island', text: 'A tiny island with a big story, and good snorkelling around it.' },
      { title: 'Toa Maru', text: 'A wartime freighter covered in coral, an easy wreck dive.' },
      { title: 'Gizo market', text: 'Fresh fish, fruit and vegetables on the waterfront.' },
    ],
  },
  {
    slug: 'arnavons', name: 'The Arnavon Islands', country: 'Solomon Islands', ports: ['ARN'],
    tagline: 'Uninhabited islands where hawksbill turtles come ashore to nest.',
    intro: [
      'The Arnavons are a small group of low, uninhabited islands between Choiseul and Santa Isabel. They are one of the most important nesting grounds for hawksbill turtles in the western Pacific, and since 1995 the surrounding communities have protected them as the Arnavon Community Marine Conservation Area.',
      'Rangers from the nearby villages live on the islands and patrol the beaches. With their permission and a ranger to guide us, we hope to see turtles nesting at night or hatchlings racing for the sea.',
    ],
    highlights: [
      { title: 'Hawksbill turtles', text: 'Nesting at night on the beaches, with luck and a ranger.' },
      { title: 'Community rangers', text: 'The people who protect the islands and their turtles.' },
      { title: 'Protected reefs', text: 'Healthy coral and fish life inside the conservation area.' },
    ],
  },
  {
    slug: 'pohnpei', name: 'Pohnpei', country: 'Federated States of Micronesia', ports: ['PNI'],
    tagline: 'A rainforest island with waterfalls everywhere and a lost city built of stone on the reef.',
    intro: [
      "Pohnpei is lush, green and one of the wettest places on Earth, which means waterfalls around every corner. It is also home to the FSM's capital, Palikir.",
      'Off its east coast lies Nan Madol, a city of nearly a hundred artificial islets built from stacked basalt columns, the seat of a ruling dynasty for centuries. It is a UNESCO World Heritage site, and you explore it by boat and on foot among the mangroves.',
    ],
    highlights: [
      { title: 'Nan Madol', text: 'Walls of giant basalt logs rising from the reef: the "Venice of the Pacific".' },
      { title: 'Waterfalls', text: 'Kepirohi and many more, a short drive and a swim away.' },
      { title: 'Sokehs Rock', text: 'A dramatic cliff above Kolonia harbour, worth the climb.' },
      { title: 'Kolonia', text: 'The little main town, with a market and a harbour full of fishing boats.' },
    ],
  },
  {
    slug: 'chuuk-outer', name: 'Chuuk and Puluwat', country: 'Federated States of Micronesia', ports: ['CHK', 'PUL'],
    tagline: 'A ghost fleet in a lagoon, and atolls where navigators still read the stars.',
    intro: [
      'Chuuk Lagoon, once called Truk, is a legend among divers. In February 1944 an air attack sank dozens of Japanese ships here, and they still sit upright on the bottom with trucks, aircraft and cargo in their holds.',
      'West of Chuuk we sail to Puluwat, an atoll famous for its traditional navigators, who crossed the open ocean in sailing canoes using stars, swells and birds. Life here is simple, traditional and generous.',
    ],
    highlights: [
      { title: 'Wreck diving', text: 'Coral-covered freighters, a sunken fleet in clear, warm water.' },
      { title: 'Traditional navigation', text: 'Meet the canoe builders and navigators of Puluwat.' },
      { title: 'Atoll life', text: 'Coconut palms, lagoons and villages with no cars at all.' },
      { title: 'Snorkelling', text: 'Reefs barely touched by visitors.' },
    ],
  },
  {
    slug: 'yap-ulithi', name: 'Yap, Ulithi and Woleai', country: 'Federated States of Micronesia', ports: ['YAP', 'ULI', 'WOL'],
    tagline: 'Giant stone money, manta rays and one of the most traditional cultures in the Pacific.',
    intro: [
      "Yap is famous for its rai: huge carved stone discs, some taller than a person, quarried in Palau and carried back by canoe. They still line village paths and still change hands, even though they never move.",
      'Yap is also known for its manta rays, which glide through the channels to cleaning stations all year. North-east lies Ulithi, an enormous atoll that sheltered the largest US fleet anchorage of the Second World War.',
      'On the way from Chuuk we stop at Woleai, one of Yap\'s outer atolls, where canoes are still sailed between islands and visitors are rare enough to be an event.',
    ],
    highlights: [
      { title: 'Stone money', text: 'Walk a stone-money bank in a village and hear the stories behind the stones.' },
      { title: 'Manta rays', text: "Watch mantas up close at Yap's cleaning stations." },
      { title: 'Traditional dance', text: 'Village dances and men\'s houses that keep old ways alive.' },
      { title: 'Ulithi', text: 'A huge, quiet lagoon with turquoise water and empty islets.' },
    ],
  },
  {
    slug: 'palau', name: 'Palau', country: 'Republic of Palau', ports: ['PAL'],
    tagline: 'Mushroom islands, a lake full of golden jellyfish and some of the best diving on the planet.',
    intro: [
      "Palau's Rock Islands are hundreds of jungle-topped limestone domes rising from turquoise water: the Southern Lagoon is a UNESCO World Heritage site, and it's one of the most beautiful places we know.",
      'Palau created the world\'s first shark sanctuary, and every visitor signs the Palau Pledge to protect it. Below the surface are walls, channels full of sharks and turtles, manta rays at German Channel, and Jellyfish Lake, where millions of golden jellyfish drift in the sun.',
    ],
    highlights: [
      { title: 'Rock Islands', text: 'Kayak and swim among the mushroom islands and hidden beaches.' },
      { title: 'Jellyfish Lake', text: 'Snorkel among golden jellyfish whose sting is too weak to feel. Check it is open when you come.' },
      { title: 'Blue Corner and German Channel', text: 'Sharks, turtles, schools of fish and manta rays.' },
      { title: 'Milky Way', text: 'A lagoon of white limestone mud; coat yourself for the photo.' },
    ],
  },
  {
    slug: 'raja-ampat', name: 'Raja Ampat', country: 'Indonesia', ports: ['SOR', 'WAY', 'KRI', 'MIS'],
    tagline: 'The richest reefs on Earth, among jungle islands where birds-of-paradise dance.',
    intro: [
      "Raja Ampat, off the western tip of New Guinea, has more species of fish and coral than anywhere else on the planet. A famous survey off Kri counted more than 300 fish species on a single dive.",
      'Above water it is just as wild: the karst islands of Wayag, mangrove lagoons in Misool, villages on stilts, and birds-of-paradise dancing in the forest at dawn.',
    ],
    highlights: [
      { title: 'Wayag', text: 'Climb to the viewpoint over jungle islands scattered across turquoise water.' },
      { title: 'Snorkelling everywhere', text: 'Off the boat, off the beach, off the jetty: the reef is right there.' },
      { title: 'Birds-of-paradise', text: "Dawn walks to see Wilson's and red birds-of-paradise display." },
      { title: 'Misool', text: 'Hidden lagoons, rock art and some of the healthiest reefs in Indonesia.' },
    ],
  },
  {
    slug: 'ternate', name: 'Ternate', country: 'Indonesia', ports: ['TER'],
    tagline: 'A volcano island at the heart of the old spice trade.',
    intro: [
      'Ternate and its neighbour Tidore were among the few islands on Earth where cloves grew, and the spice drew Portuguese, Spanish and Dutch ships across the world. Forts from those days still stand along the shore.',
      'The whole island is one volcano, Gamalama, and the town wraps around its base. It is a lively, friendly place to stop between Raja Ampat and the Philippines.',
    ],
    highlights: [
      { title: 'Spice history', text: 'Colonial forts and the old sultan\'s palace.' },
      { title: 'Gamalama', text: 'An active volcano you can see from everywhere on the island.' },
      { title: 'Clove gardens', text: 'Clove trees on the green slopes above town.' },
      { title: 'Markets', text: 'Fresh fish and spices straight from the island.' },
    ],
  },
  {
    slug: 'cebu', name: 'Cebu', country: 'Philippines', ports: ['CEB'],
    tagline: 'Sardine runs, thresher sharks and the best roast pig in the Philippines.',
    intro: [
      "Cebu is where Magellan landed in 1521, and the cross he planted is still kept in the city. Today it is the Philippines' second city, a hub for flights, and a great place to restock and eat very well.",
      'A day trip away the sea steals the show: millions of sardines swirl off Moalboal, thresher sharks rise at dawn off Malapascua, and Kawasan Falls tumbles into turquoise pools.',
    ],
    highlights: [
      { title: 'Moalboal sardine run', text: 'Snorkel into a shimmering wall of sardines just off the beach.' },
      { title: 'Malapascua', text: 'An early dive to see thresher sharks.' },
      { title: 'Lechon', text: "Cebu's famous roast pig. Bring an appetite." },
      { title: 'Kawasan Falls', text: 'Turquoise pools and waterfalls in the hills.' },
    ],
  },
  {
    slug: 'el-nido', name: 'El Nido and Palawan', country: 'Philippines', ports: ['ELN', 'PPS'],
    tagline: 'Limestone cliffs, secret lagoons and an underground river.',
    intro: [
      'El Nido is Palawan at its most dramatic: black limestone cliffs rising straight out of Bacuit Bay, hidden lagoons you paddle into through gaps in the rock, and beaches on almost every island.',
      'From Puerto Princesa you can ride a boat deep into the Puerto Princesa Subterranean River, a UNESCO World Heritage site that winds for kilometres under the mountains to the sea.',
    ],
    highlights: [
      { title: 'Big and Small Lagoon', text: 'Kayak through a crack in the cliff into still, emerald water.' },
      { title: 'Island hopping', text: 'A different beach, cove and reef every day.' },
      { title: 'Nacpan Beach', text: 'Four kilometres of golden sand with hardly anyone on it.' },
      { title: 'Underground River', text: 'Bats, rock formations and a river running under the mountains.' },
    ],
  },
  {
    slug: 'sabah', name: 'Sabah', country: 'Malaysia (Borneo)', ports: ['KUD', 'KK', 'LAB'],
    tagline: "Borneo at last: Southeast Asia's highest mountain, island parks and the tip of the island.",
    intro: [
      "Kota Kinabalu is a lively waterfront city with famous sunsets, night markets and islands a short boat ride away. Behind it rises Mount Kinabalu, 4,095 metres high, the centrepiece of a UNESCO-listed park.",
      'At the northern tip of Borneo, near Kudat, the South China Sea meets the Sulu Sea. Further south, Labuan is a duty-free island with some of the best wreck diving in Malaysia.',
    ],
    highlights: [
      { title: 'Mount Kinabalu', text: 'Hike its trails, or just admire it from the coast at sunrise.' },
      { title: 'Island parks', text: 'Snorkel and picnic in Tunku Abdul Rahman Park off Kota Kinabalu.' },
      { title: 'Tip of Borneo', text: 'Stand at the northernmost point of the island.' },
      { title: 'Night markets', text: 'Grilled fish, satay and tropical fruit by the waterfront.' },
    ],
  },
  {
    slug: 'sarawak', name: 'Sarawak', country: 'Malaysia (Borneo)', ports: ['MIR', 'KCH'],
    tagline: 'Orangutans, proboscis monkeys, giant caves and the city of cats.',
    intro: [
      'Kuching, the capital of Sarawak, is one of the most charming cities in Southeast Asia, with a riverside promenade, old shophouses and a soft spot for cats. It is also the home of Sarawak laksa, worth the trip alone.',
      "From Kuching you can watch orangutans at Semenggoh and look for proboscis monkeys in Bako National Park. Up the coast from Miri lie Gunung Mulu, a UNESCO site whose caves are among the largest on Earth, and the ancient Niah Caves.",
    ],
    highlights: [
      { title: 'Semenggoh orangutans', text: 'Semi-wild orangutans coming in from the forest at feeding time.' },
      { title: 'Bako National Park', text: 'Proboscis monkeys, sea stacks and jungle trails.' },
      { title: 'Gunung Mulu', text: 'Giant caves and a dusk exodus of millions of bats, a short flight from Miri.' },
      { title: 'Sarawak laksa', text: 'A spicy, tangy noodle soup for breakfast.' },
    ],
  },
  {
    slug: 'singapore', name: 'Singapore', country: 'Singapore', ports: ['SIN'],
    tagline: 'The finish line for this leg: hawker food, Supertrees and a celebration.',
    intro: [
      'Singapore is where this chapter of the voyage ends, and it is a great place to celebrate. The city is green, safe and easy, with some of the best-connected flights in the world.',
      'Eat your way through the hawker centres, now recognised by UNESCO as living heritage, walk the Supertree Grove at Gardens by the Bay at night, and watch the ships at anchor off Marina Bay knowing we sailed here.',
    ],
    highlights: [
      { title: 'Hawker centres', text: 'Chicken rice, laksa and chilli crab for a few dollars.' },
      { title: 'Gardens by the Bay', text: 'The Supertrees light up after dark.' },
      { title: 'Marina Bay', text: 'The skyline from the water, the end of a long passage.' },
      { title: 'Celebration', text: 'Join us for the finish, wherever the plan lands.' },
    ],
  },
];

const PHOTOS = photosJson as Record<string, Photo[]>;

/** Steve's own pictures: no licence to cite, credited to the boat. */
export const own = (name: string, alt: string): Photo => ({ file: `/places/own/${name}.jpg`, alt, credit: 'SV COS', license: '', licenseUrl: '', source: '' });

/** Steve's pictures placed among a story's Commons photos, after its hero. */
const OWN: Record<string, Photo[]> = {
  pohnpei: [own('pohnpei-atoll', 'Palm-covered islets around a turquoise atoll lagoon')],
  tanna: [own('tanna-eruption', 'Watching the volcano erupt at night from the crater rim'), own('tanna-dance', 'A kastom dance in a village clearing')],
  'malekula-ambrym': [own('ambrym-village', 'A village gathering beside carved slit drums')],
  'el-nido': [own('coron-lagoon', 'Boats in a hidden lagoon among limestone cliffs, Palawan')],
  sarawak: [own('borneo-orangutan', 'An orangutan mother and baby in the rainforest')],
};

export const PLACES: Place[] = STORIES.map(s => {
  const [hero, ...rest] = PHOTOS[s.slug] ?? [];
  return { ...s, photos: [...(hero ? [hero] : []), ...(OWN[s.slug] ?? []), ...rest] };
});
export const placeBySlug = new Map(PLACES.map(p => [p.slug, p]));

/** The place a set of stops belongs to: the first place holding any of the ports. */
export function placeForPorts(ports: string[]): Place | null {
  for (const p of PLACES) if (ports.some(id => p.ports.includes(id))) return p;
  return null;
}
